import React from 'react';
import useImpersonation from '../hooks/useImpersonation';

/**
 * ImpersonationBanner
 *
 * Persistent red strip across the top of every authenticated page while a
 * platform_superuser has an active impersonation session. SOC 2 CC6.1 / CC6.3:
 * makes the elevated-privilege state visually impossible to miss and provides
 * a one-click "End impersonation" action.
 *
 * Mount this once inside ProtectedApp (and any other authenticated shell) at
 * the top of the layout. It renders nothing when no session is active, so it's
 * safe to leave mounted everywhere.
 */
export default function ImpersonationBanner() {
  const { active, target_org, minutesRemaining, mode, endImpersonation } = useImpersonation();

  if (!active) return null;

  const handleEnd = async () => {
    const ok = window.confirm(
      `End impersonation of ${target_org?.name || 'this organization'}? You'll return to the Platform Admin Console.`
    );
    if (!ok) return;
    await endImpersonation();
    window.location.href = '/platform-admin';
  };

  const minutesLabel = (() => {
    if (minutesRemaining === null || minutesRemaining === undefined) return 'session active';
    if (minutesRemaining <= 0) return 'expiring now';
    if (minutesRemaining === 1) return '1 min remaining';
    return `${minutesRemaining} min remaining`;
  })();

  const modeLabel = mode === 'read_only' ? 'read-only' : (mode || 'read-only');

  return (
    <div
      role="alert"
      aria-live="polite"
      className="flex-shrink-0 bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg border-b border-red-900/40"
    >
      <div className="px-6 py-2.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-xl leading-none" aria-hidden="true">&#128065;</span>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 min-w-0">
            <span className="text-sm font-semibold tracking-tight">
              Viewing as{' '}
              <span className="underline decoration-red-200 underline-offset-2">
                {target_org?.name || 'client organization'}
              </span>
            </span>
            <span className="text-xs uppercase tracking-wider bg-white/15 border border-white/25 rounded-full px-2 py-0.5 font-semibold">
              {modeLabel}
            </span>
            <span className="text-xs text-red-100 font-medium">{minutesLabel}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={handleEnd}
            className="px-3.5 py-1.5 rounded-lg bg-white text-red-700 text-xs font-semibold hover:bg-red-50 transition shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-red-700"
          >
            End impersonation
          </button>
        </div>
      </div>
    </div>
  );
}
